import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Input,
  Text,
  toastManager,
  type Color,
} from "@salestrak/ui";

import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { EntityTag } from "#app/components/entity";
import type { Omittable } from "@salestrak/ui";

type CreateStageDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  stageType: string;
  sortOrder?: number;
  onCreated?: () => void;
}>;

const PALETTE_COLORS: Color[] = [
  "dark-grey", "grey", "red", "orange", "yellow",
  "light-green", "green", "teal", "cyan", "light-blue",
  "blue", "purple", "light-purple", "violet", "magenta", "pink",
];

const DEFAULT_COLORS: Record<string, Color> = {
  pending: "dark-grey",
  inProgress: "teal",
  done: "purple",
  canceled: "dark-grey",
};

export function CreateStageDialog({
  open,
  onOpenChange,
  workflowId,
  stageType,
  sortOrder,
  onCreated,
}: CreateStageDialogProps) {
  const { createStage } = useStageMutations();

  const [name, setName] = useState("");
  const [color, setColor] = useState<Color>(DEFAULT_COLORS[stageType] ?? "teal");
  const [nameError, setNameError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setColor(DEFAULT_COLORS[stageType] ?? "teal");
      setNameError("");
      setSubmitting(false);
    }
  }, [open, stageType]);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setNameError("");
  }, []);

  const canSubmit = name.trim().length >= 3 && !submitting;

  const handleSubmit = useCallback(async () => {
    if (name.trim().length < 3) {
      setNameError("Name must be at least 3 characters");
      return;
    }
    if (name.trim().length > 64) {
      setNameError("Name must be at most 64 characters");
      return;
    }

    setSubmitting(true);
    try {
      const result = await createStage({
        workflowId,
        name: name.trim(),
        color,
        type: stageType,
        sortOrder,
      });

      if (!result) {
        toastManager.add({ title: "Failed to create stage", type: "error" });
        return;
      }

      if ("__typename" in result) {
        if (result.__typename === "ValidationError") {
          toastManager.add({
            title:
              "message" in result
                ? String((result as { message?: unknown }).message)
                : "Validation error",
            type: "error",
          });
          return;
        }
        if (result.__typename === "PermissionDenied") {
          toastManager.add({
            title: "You don't have permission to create stages",
            type: "error",
          });
          return;
        }
      }

      toastManager.add({ title: "Stage created", type: "success" });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toastMutationError(err, "Failed to create stage");
    } finally {
      setSubmitting(false);
    }
  }, [name, color, stageType, workflowId, sortOrder, createStage, onOpenChange, onCreated]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Create stage</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <Box direction="column" gap="lg">
            <Box direction="column" gap="sm">
              <Box direction="row" gap="sm" align="center">
                <EntityTag
                  entity={{
                    kind: "stage",
                    // Not yet persisted — preview-only glyph, no real stage id.
                    id: "stage-preview",
                    stage: { type: stageType, icon: { color, percentage: 50 } },
                  }}
                  display="visual"
                  size="md"
                  interactive={false}
                />
                <Text size="sm" weight="medium">Color</Text>
              </Box>
              <Box direction="row" gap="xs" wrap>
                {PALETTE_COLORS.map((c) => (
                  <ColorSwatch
                    key={c}
                    aria-label={c}
                    color={c}
                    size="xl"
                    rounded="md"
                    selected={c === color}
                    onClick={() => setColor(c)}
                  />
                ))}
              </Box>
            </Box>

            <Box direction="column" gap="xs">
              <Text size="sm" weight="medium">Name</Text>
              <Input
                placeholder="Stage name..."
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                aria-invalid={!!nameError}
                autoFocus
              />
              {nameError ? (
                <Text size="xs" variant="destructive">{nameError}</Text>
              ) : null}
            </Box>
          </Box>
        </DialogPanel>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
            Create
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
